import { apiService } from './api';

export interface SessionPolicy {
  sessionTimeoutMinutes: number;
  tokenExpirationMinutes: number;
  maxLoginAttempts?: number;
}

export interface PublicSystemConfig {
  securitySettings?: {
    sessionTimeoutMinutes: number;
    maxLoginAttempts: number;
    tokenExpirationMinutes?: number;
  };
  sessionTimeoutMinutes?: number;
  tokenExpirationMinutes?: number;
  maxLoginAttempts?: number;
  apiSettings: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
    enableCaching: boolean;
    cacheTimeout: number;
  };
  contentSettings: {
    maxVocabularyPerLevel: number;
    maxGrammarLessonsPerLevel: number;
    maxDialoguesPerLevel: number;
    maxQuizzesPerLevel: number;
    defaultQuizTimeLimit: number;
    enableAudioFeatures: boolean;
  };
  persianSettings: {
    defaultFont: string;
    enableRTL: boolean;
    enableBidirectionalText: boolean;
    persianNumberFormatting: boolean;
  };
}

export interface SystemStatus {
  isHealthy: boolean;
  version: string;
  uptime: string;
  environment: string;
}

export interface StaticPageContentDto {
  content: string;
  contentFa?: string | null;
}

export type StaticPagesMap = Record<string, StaticPageContentDto>;

function normalizeStaticPageEntry(raw: unknown): StaticPageContentDto {
  if (typeof raw === 'string') return { content: raw, contentFa: null };
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    return {
      content: String(o.content ?? o.Content ?? ''),
      contentFa: (o.contentFa ?? o.ContentFa ?? null) as string | null,
    };
  }
  return { content: '', contentFa: null };
}

export function normalizeStaticPagesMap(raw: unknown): StaticPagesMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: StaticPagesMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    out[key] = normalizeStaticPageEntry(value);
  }
  return out;
}

export function pickStaticPageHtml(page: StaticPageContentDto | undefined, locale: 'en' | 'fa'): string {
  if (!page) return '';
  if (locale === 'fa') {
    const fa = page.contentFa?.trim();
    if (fa) return fa;
  }
  return page.content?.trim() ?? '';
}

/** Normalize flat or nested security fields from the API response. */
export function normalizeSessionPolicy(raw: PublicSystemConfig | Record<string, unknown> | null | undefined): SessionPolicy {
  const r = (raw ?? {}) as Record<string, unknown>;
  const nested = r.securitySettings as Record<string, unknown> | undefined;
  const sessionTimeoutMinutes =
    Number(nested?.sessionTimeoutMinutes ?? r.sessionTimeoutMinutes ?? r.SessionTimeoutMinutes) || 60;
  const tokenExpirationMinutes =
    Number(nested?.tokenExpirationMinutes ?? r.tokenExpirationMinutes ?? r.TokenExpirationMinutes) || 60;
  const maxLoginAttempts =
    Number(nested?.maxLoginAttempts ?? r.maxLoginAttempts ?? r.MaxLoginAttempts) || 5;
  return { sessionTimeoutMinutes, tokenExpirationMinutes, maxLoginAttempts };
}

class PublicSystemConfigService {
  /**
   * Session policy (anonymous) — used to configure client inactivity timeout before login.
   */
  async getSessionPolicy(): Promise<SessionPolicy> {
    try {
      const policy = await apiService.get<PublicSystemConfig | SessionPolicy>('/api/System/session-policy', {
        cache: false,
      });
      return normalizeSessionPolicy(policy);
    } catch (error) {
      console.warn('PublicSystemConfigService: Failed to load session policy, using defaults:', error);
      return { sessionTimeoutMinutes: 60, tokenExpirationMinutes: 60, maxLoginAttempts: 5 };
    }
  }

  /**
   * Get public system configuration (available to all authenticated users)
   */
  async getPublicConfig(): Promise<PublicSystemConfig> {
    try {
      console.log('PublicSystemConfigService: Fetching public system config');
      const config = await apiService.get<PublicSystemConfig>('/api/System/config');
      console.log('PublicSystemConfigService: Successfully loaded public config:', config);
      const policy = normalizeSessionPolicy(config);
      return {
        ...config,
        securitySettings: {
          sessionTimeoutMinutes: policy.sessionTimeoutMinutes,
          tokenExpirationMinutes: policy.tokenExpirationMinutes,
          maxLoginAttempts: policy.maxLoginAttempts ?? 5,
        },
      };
    } catch (error) {
      console.error('PublicSystemConfigService: Failed to load public config:', error);
      throw error;
    }
  }

  /**
   * Get system status and health information
   */
  async getSystemStatus(): Promise<SystemStatus> {
    try {
      console.log('PublicSystemConfigService: Fetching system status');
      const status = await apiService.get<SystemStatus>('/api/System/status');
      console.log('PublicSystemConfigService: Successfully loaded system status:', status);
      return status;
    } catch (error) {
      console.error('PublicSystemConfigService: Failed to load system status:', error);
      throw error;
    }
  }

  /**
   * Get static site page contents (About, Contact, Privacy, Terms). Public, no auth required.
   */
  async getStaticPages(): Promise<StaticPagesMap> {
    try {
      const contents = await apiService.get<unknown>('/api/System/static-pages', { cache: true });
      return normalizeStaticPagesMap(contents);
    } catch (error) {
      console.error('PublicSystemConfigService: Failed to load static pages:', error);
      return {};
    }
  }
}

export const publicSystemConfigService = new PublicSystemConfigService();
